import { useEffect, useReducer, useState } from 'react';

import type { LDFlagSet } from 'launchdarkly-js-client-sdk';

import type { LaunchDarklyContextValue } from './LaunchDarklyContext';
import {
  getLocalStorageFlagOverride,
  getLocalStorageOverrides,
  useOnOverrideChange,
} from './LaunchDarklyLocalStorage';
import {
  getErrorSuffix,
  useReportErrorOnce,
  useWarnMissingDefaultOnce,
} from './launchDarklyUtils';

export const useFeatureFlagCore = <FlagValue>(
  ldContext: LaunchDarklyContextValue | undefined,
  flagName: string,
  defaultValue?: FlagValue,
) => {
  useWarnMissingDefaultOnce(flagName, defaultValue);
  const [, forceUpdate] = useReducer((n: number) => n + 1, 0);
  useOnOverrideChange(forceUpdate);
  const reportErrorOnce = useReportErrorOnce();

  if (!ldContext) {
    return { loading: true, value: defaultValue };
  }

  const { loading, client } = ldContext;

  try {
    const lsOverride = getLocalStorageFlagOverride<FlagValue>(flagName);
    if (lsOverride.isOverridden) {
      return { loading: false, value: lsOverride.value };
    }
  } catch (error) {
    console.warn(
      `LaunchDarkly error reading localStorage flag '${flagName}':`,
      error,
    );
  }

  if (loading || !client.current) {
    return { loading, value: defaultValue };
  }

  try {
    const value = client.current.variation(flagName, defaultValue);
    return { loading, value };
  } catch (error) {
    reportErrorOnce(
      `LaunchDarkly error reading flag ${flagName}${getErrorSuffix(error)}`,
      { flagName },
    );
    return { loading: false, value: defaultValue };
  }
};

export const useFeatureFlagOnceCore = <FlagValue>(
  result: { value: FlagValue | undefined; loading: boolean },
  defaultValue?: FlagValue,
) => {
  const { value, loading } = result;
  const [stableLoading, setStableLoading] = useState<boolean>(true);
  const [stableValue, setStableValue] = useState<FlagValue | undefined>(
    defaultValue,
  );

  useEffect(() => {
    if (!loading && stableLoading === true) {
      setStableLoading(false);
    }
  }, [loading, stableLoading]);

  useEffect(() => {
    if (!loading && value !== stableValue) {
      setStableValue(value);
    }
  }, [loading, stableValue, value]);

  return { value: stableValue, loading: stableLoading };
};

export const useAllFeatureFlagsCore = (
  ldContext: LaunchDarklyContextValue | undefined,
) => {
  const [, forceUpdate] = useReducer((n: number) => n + 1, 0);
  useOnOverrideChange(forceUpdate);
  const reportErrorOnce = useReportErrorOnce();
  const emptyFlags: LDFlagSet = {};

  if (!ldContext) {
    return { loading: true, value: emptyFlags };
  }

  const { loading, client } = ldContext;

  if (loading || !client.current) {
    return { loading, value: emptyFlags };
  }

  try {
    const value = {
      ...client.current.allFlags(),
      ...getLocalStorageOverrides(),
    };
    return { loading, value };
  } catch (error) {
    reportErrorOnce(
      `LaunchDarkly error reading all flags${getErrorSuffix(error)}`,
    );
    return { loading: false, value: emptyFlags };
  }
};
